import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DoctorNavbar from '../components/DoctorNavbar';
import { toast } from 'react-toastify';

function DoctorProfile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [doctorId, setDoctorId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone_number: '',
    address: '',
    specialization: '',
    qualification: '',
    experience: '',
    consultation_fees: '',
    audio_consultation_fees: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const loadProfile = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const session = await api.get('doctor/session-check/');
      if (!session.data.authenticated) {
        navigate('/doctor/login');
        return;
      }
      const did = session.data.doctor_id;
      setDoctorId(did);

      const res = await api.get(`doctor/profile/${did}/`);
      setProfile(res.data);
      setImagePreview(null);

      setForm({
        name: res.data.name || '',
        email: res.data.email || '',
        phone_number: res.data.phone_number || '',
        address: res.data.address || '',
        specialization: res.data.specialization || '',
        qualification: res.data.qualification || '',
        experience: res.data.experience || '',
        consultation_fees: res.data.consultation_fees || '',
        audio_consultation_fees: res.data.audio_consultation_fees || '',
      });
    } catch (err) {
      console.error('Failed to load doctor profile:', err);
      if (err.response?.status === 401) {
        navigate('/doctor/login');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('blob:')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    return `${apiUrl}/media/${image}`;
  };

  const displayImage =
    imagePreview || getImageUrl(profile?.image) || 'https://via.placeholder.com/150';

  const openImagePicker = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }

    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    handleImageUpload(file);
  };

  const handleImageUpload = async (file) => {
    if (!doctorId) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      await api.patch(`doctor/profile/update/${doctorId}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await loadProfile({ silent: true });
      toast.success('Profile image updated successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Error uploading image:', err);
      setImagePreview(null);
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.msg ||
        (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : '') ||
        'Failed to update image. Please try again.';
      toast.error(errorMsg, {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const resetFormFromProfile = () => {
    if (!profile) return;
    setForm({
      name: profile.name || '',
      email: profile.email || '',
      phone_number: profile.phone_number || '',
      address: profile.address || '',
      specialization: profile.specialization || '',
      qualification: profile.qualification || '',
      experience: profile.experience || '',
      consultation_fees: profile.consultation_fees || '',
      audio_consultation_fees: profile.audio_consultation_fees || '',
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (form[key] !== null && form[key] !== undefined && form[key] !== '') {
          formData.append(key, form[key]);
        }
      });

      const response = await api.patch(`doctor/profile/update/${doctorId}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setEditMode(false);
      await loadProfile();
      toast.success(response.data?.msg || 'Profile updated successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      console.error('Failed to update profile:', err.response?.data || err.message);
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.msg ||
        (err.response?.data && typeof err.response.data === 'object'
          ? Object.values(err.response.data).flat().join(', ')
          : '') ||
        'Failed to update profile. Please try again.';
      toast.error(errorMsg, {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <DoctorNavbar />

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-link text-decoration-none text-muted p-0 me-3"
                  onClick={() => navigate(-1)}
                >
                  <i className="bi bi-arrow-left fs-4"></i>
                </button>
                <h2 className="fw-bold mb-0">Professional Profile</h2>
              </div>
              {!editMode && (
                <button
                  className="btn btn-primary rounded-pill px-4 shadow-sm"
                  onClick={() => setEditMode(true)}
                >
                  <i className="bi bi-pencil-square me-2"></i> Edit Profile
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={handleImageChange}
            />

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-body p-0">
                <div className="row g-0">
                  <div className="col-md-4 bg-primary text-white p-5 d-flex flex-column align-items-center justify-content-center text-center">
                    <div className="position-relative mb-4">
                      <div className="bg-white rounded-circle p-1 shadow-lg">
                        <img
                          src={displayImage}
                          alt="Profile"
                          className="rounded-circle object-fit-cover"
                          style={{
                            width: '150px',
                            height: '150px',
                            cursor: 'pointer',
                            opacity: uploadingImage ? 0.6 : 1,
                          }}
                          onClick={openImagePicker}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/150';
                          }}
                          title="Click to change photo"
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-light btn-sm rounded-circle shadow position-absolute d-flex align-items-center justify-content-center"
                        style={{
                          width: 40,
                          height: 40,
                          right: 4,
                          bottom: 4,
                          border: '2px solid #fff',
                        }}
                        onClick={openImagePicker}
                        disabled={uploadingImage}
                        title="Change profile photo"
                      >
                        {uploadingImage ? (
                          <span className="spinner-border spinner-border-sm text-primary" />
                        ) : (
                          <i className="bi bi-camera-fill text-primary"></i>
                        )}
                      </button>
                    </div>
                    <p className="small opacity-75 mb-3">
                      {uploadingImage ? 'Uploading…' : 'Tap photo to change'}
                    </p>
                    <h3 className="fw-bold mb-1">Dr. {profile?.name}</h3>
                    <p className="badge bg-light text-primary fs-6 mb-3">
                      {profile?.specialization || 'General Practitioner'}
                    </p>
                    <div className="d-flex align-items-center small opacity-75">
                      <i className="bi bi-geo-alt-fill me-2"></i>
                      {profile?.address || 'Address not set'}
                    </div>
                  </div>

                  <div className="col-md-8 p-5">
                    {!editMode ? (
                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Full Name
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.name}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Email Address
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.email}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Phone Number
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.phone_number || '—'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Specialization
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.specialization || '—'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Qualification
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.qualification || '—'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Experience (Years)
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.experience || '—'}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Video Consultation Fees (USD $)
                          </label>
                          <p className="fs-5 fw-medium mb-0">
                            {profile?.consultation_fees ?? '—'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Audio Consultation Fees (USD $)
                          </label>
                          <p className="fs-5 fw-medium mb-0">
                            {profile?.audio_consultation_fees ?? '—'}
                          </p>
                        </div>
                        <div className="col-12">
                          <label className="text-muted small text-uppercase fw-bold mb-1 d-block">
                            Clinic Address
                          </label>
                          <p className="fs-5 fw-medium mb-0">{profile?.address || '—'}</p>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleUpdate}>
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="form-label fw-bold small">Profile Photo</label>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <img
                                src={displayImage}
                                alt="Preview"
                                className="rounded-circle object-fit-cover border"
                                style={{ width: 72, height: 72 }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/72';
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-primary rounded-pill"
                                onClick={openImagePicker}
                                disabled={uploadingImage}
                              >
                                <i className="bi bi-image me-2"></i>
                                {uploadingImage ? 'Uploading…' : 'Change Photo'}
                              </button>
                              <small className="text-muted">JPG/PNG, max 5 MB</small>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">Full Name</label>
                            <input
                              type="text"
                              className="form-control rounded-3"
                              value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">Email Address</label>
                            <input
                              type="email"
                              className="form-control rounded-3"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">Phone Number</label>
                            <input
                              type="tel"
                              className="form-control rounded-3"
                              value={form.phone_number}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setForm({ ...form, phone_number: value });
                              }}
                              maxLength={10}
                              pattern="[0-9]{10}"
                              inputMode="numeric"
                            />
                            <small className="text-muted">10 digits only</small>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">Specialization</label>
                            <input
                              type="text"
                              className="form-control rounded-3"
                              value={form.specialization}
                              onChange={(e) =>
                                setForm({ ...form, specialization: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">Experience (Years)</label>
                            <input
                              type="number"
                              className="form-control rounded-3"
                              value={form.experience}
                              onChange={(e) => setForm({ ...form, experience: e.target.value })}
                              min="0"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">Qualification</label>
                            <input
                              type="text"
                              className="form-control rounded-3"
                              value={form.qualification}
                              onChange={(e) =>
                                setForm({ ...form, qualification: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">
                              Video Consultation Fees (USD $)
                            </label>
                            <input
                              type="number"
                              className="form-control rounded-3"
                              value={form.consultation_fees}
                              onChange={(e) =>
                                setForm({ ...form, consultation_fees: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small">
                              Audio Consultation Fees (USD $)
                            </label>
                            <input
                              type="number"
                              className="form-control rounded-3"
                              value={form.audio_consultation_fees}
                              onChange={(e) =>
                                setForm({ ...form, audio_consultation_fees: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label fw-bold small"> Address</label>
                            <textarea
                              className="form-control rounded-3"
                              rows="3"
                              value={form.address}
                              onChange={(e) => setForm({ ...form, address: e.target.value })}
                            ></textarea>
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-4">
                          <button
                            type="submit"
                            className="btn btn-success rounded-pill px-4 shadow-sm"
                            disabled={saving}
                          >
                            {saving ? (
                              <span className="spinner-border spinner-border-sm me-2" />
                            ) : (
                              <i className="bi bi-check-lg me-2"></i>
                            )}
                            Save Changes
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => {
                              setEditMode(false);
                              resetFormFromProfile();
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorProfile;
